/**
 * Cliente HTTP do front. Fonte: docs/13 (envelope {data} / {error}).
 * credentials:"include" leva o cookie de sessão (same-origin, ADR-003).
 */
import type { ApiErrorBody } from "@unik/shared";

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiClientError";
    this.code = body.error.code;
    this.status = status;
    this.fields = body.error.fields;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const json = (await res.json().catch(() => null)) as unknown;

  if (!res.ok) {
    const fallback: ApiErrorBody = {
      error: { code: "INTERNAL_ERROR", message: "Falha de conexão. Tente de novo.", requestId: "" },
    };
    throw new ApiClientError(res.status, (json as ApiErrorBody | null) ?? fallback);
  }

  return (json as { data: T }).data;
}

/** Upload multipart — o navegador define o Content-Type com o boundary. */
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const json = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const fallback: ApiErrorBody = {
      error: { code: "INTERNAL_ERROR", message: "Falha ao enviar. Tente de novo.", requestId: "" },
    };
    throw new ApiClientError(res.status, (json as ApiErrorBody | null) ?? fallback);
  }
  return (json as { data: T }).data;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>("GET", path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>("PATCH", path, body),
  del: <T>(path: string): Promise<T> => request<T>("DELETE", path),
  delete: <T>(path: string): Promise<T> => request<T>("DELETE", path),
  upload: <T>(path: string, form: FormData): Promise<T> => requestForm<T>(path, form),
};
